import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Link from '@mui/material/Link';
import './styles.less';

export interface ExpiryBannerProps {
  isLocked?: boolean;
  isExpired: boolean;
  isOwner: boolean;
  hasExpirationDate: boolean;
  formattedDate: string | null;
  openCalendar: () => void;
  modal: ReactNode;
}

const ExpiryBanner = ({
  isLocked = false,
  isExpired,
  isOwner,
  hasExpirationDate,
  formattedDate,
  openCalendar,
  modal,
}: ExpiryBannerProps) => {
  const className = 'expiry-banner';
  const { t } = useTranslation();

  const Wrapper = ({ children }: { children: ReactNode }) => <div className={className}>{children}</div>;

  if (isLocked) {
    return <Wrapper>{t('document.preview.banner')}</Wrapper>;
  }

  const ChangeLinkComponent = ({ children }: { children?: ReactNode }) => (
    <Link className={`${className}__link`} component="button" type="button" onClick={openCalendar}>
      {children}
    </Link>
  );

  if (isExpired) {
    return (
      <Wrapper>
        {isOwner ? (
          <>
            <Trans i18nKey="document.preview.expiration.owner.expired" components={{ changeLink: <ChangeLinkComponent /> }} />
            {modal}
          </>
        ) : (
          t('document.preview.expiration.expired', { date: formattedDate })
        )}
      </Wrapper>
    );
  }

  if (hasExpirationDate) {
    return (
      <Wrapper>
        {isOwner ? (
          <>
            <Trans
              i18nKey="document.preview.expiration.owner.upcoming"
              values={{ date: formattedDate }}
              components={{ changeLink: <ChangeLinkComponent /> }}
            />
            {modal}
          </>
        ) : (
          t('document.preview.expiration.upcoming', { date: formattedDate })
        )}
      </Wrapper>
    );
  }

  return null;
};

export default ExpiryBanner;
